import { useState, useEffect } from "react";
import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { Label } from "@/modern/components/ui/label";
import { Input } from "@/modern/components/ui/input";
import { Card } from "@/modern/components/ui/card";
import { Button } from "@/modern/components/ui/button";
import { ExternalLink, Info, AlertCircle, CheckCircle2, X, Copy, Link2 } from "lucide-react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { updateSwivuAssistText, getSwivuAssitText } from "@/network/network";
import { toast } from "sonner";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { transformSwivuGridRecords } from "@/other/utils/utils";
import { SERVER_BASE_URL } from "@/env";
import { cn } from "@/modern/utils";

const ASSIST_STEPS = [
  {
    number: 1,
    title: "Start with the Template",
    content: "From the File menu, select Make a Copy to create your own editable version.",
    hasLink: true
  },
  {
    number: 2,
    title: "Edit Your Copy",
    content: ["Update the content to match your needs.", "IMPORTANT: Do not change the ID column. This column maps each row to the correct Swivu Assist surface."],
    tip: 'Tip: To find the ID of a Swivu Assist surface, hover over the "i" icon in that surface\'s header.'
  },
  {
    number: 3,
    title: "Share Your Sheet",
    content: ['In Google Sheets, go to Share settings and set access to "Anyone with the link" as Viewer.', "Copy the share link."]
  },
  {
    number: 4,
    title: "Paste and Update",
    content: "Paste the share link into the field below, then click Update."
  },
  {
    number: 5,
    title: "Need to Go Back?",
    content: "To revert to Swivu's default help content, simply delete your link from the box and click Update."
  }
];

const SwivuAssistDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project, user, selectedBoard, userPreferences } = useSwivuBryntumContext();
  const [googleSheetUrl, setGoogleSheetUrl] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [isValidUrl, setIsValidUrl] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [hasCustomContent, setHasCustomContent] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  const isSuperUser = user?.is_superuser ?? false;
  // @ts-expect-error - selectedBoard.record is dynamically typed
  const isBoardOwner = selectedBoard?.record?.isOwner;
  const canEdit = isSuperUser || isBoardOwner;

  useEffect(() => {
    loadSheetUrl();
  }, [selectedBoard, userPreferences]);

  const loadSheetUrl = () => {
    if (!userPreferences) return;

    setIsLoading(true);
    const url = userPreferences?.getSetting(PERSISTENCE_STATE_KEYS.swivuAssistSheetUrl, "", true);
    const urlValue = url || "";
    setGoogleSheetUrl(urlValue);
    setIsValidUrl(validateGoogleSheetsUrl(urlValue));
    setHasCustomContent(!!urlValue);
    setIsLoading(false);
  };

  const validateGoogleSheetsUrl = (url: string): boolean => {
    if (url === "") {
      return true;
    }

    url = url.trim();

    // Google Sheets URL patterns
    const googleSheetsPatterns = [
      // Standard sharing URL
      /^https:\/\/docs\.google\.com\/spreadsheets\/d\/[a-zA-Z0-9-_]+\/edit(\?[^#]*)?(#.*)?$/,
      // Export URL
      /^https:\/\/docs\.google\.com\/spreadsheets\/d\/[a-zA-Z0-9-_]+\/export\?format=csv(&.*)?$/,
      // Alternative sharing URL format
      /^https:\/\/docs\.google\.com\/spreadsheets\/d\/[a-zA-Z0-9-_]+\/.*$/
    ];

    return googleSheetsPatterns.some((pattern) => pattern.test(url));
  };

  const handleUrlChange = (value: string) => {
    setGoogleSheetUrl(value);
    const isValid = validateGoogleSheetsUrl(value);
    setIsValidUrl(isValid);
    setUpdateSuccess(false); // Reset success state when URL changes
  };

  const handleClearUrl = () => {
    setGoogleSheetUrl("");
    setIsValidUrl(true);
    setHasCustomContent(false);
    setUpdateSuccess(false);
  };

  const handleCopyTemplateLink = async () => {
    const templateUrl = `${SERVER_BASE_URL}/swivuassist/template-url`;
    try {
      await navigator.clipboard.writeText(templateUrl);
      toast.success("Template link copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  const refreshSwivuAssistContent = async (boardId: string) => {
    try {
      const resp = await getSwivuAssitText(boardId);
      transformSwivuGridRecords(resp.data);
      // Trigger global update to refresh all Swivu Assist components
      // Note: triggerSwivuAssistUpdate() is called from the global scope
      if (typeof window !== "undefined" && (window as any).triggerSwivuAssistUpdate) {
        (window as any).triggerSwivuAssistUpdate();
      }
    } catch (error) {
      console.error("Error refreshing Swivu Assist content:", error);
    }
  };

  const updateSwivuAssist = async (boardId?: string) => {
    setIsUpdating(true);
    try {
      // @ts-expect-error - updateSwivuAssistText accepts string or null
      const resp = await updateSwivuAssistText(boardId || null);
      toast.success(resp.data.message || "Swivu Assist updated successfully");

      // @ts-expect-error - selectedBoard.id is dynamically typed
      await refreshSwivuAssistContent(selectedBoard?.id);

      // Set success state and update hasCustomContent
      setUpdateSuccess(true);
      setHasCustomContent(!!googleSheetUrl);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Something went wrong. Unable to update Swivu Assist text");
      setUpdateSuccess(false);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateGlobal = async () => {
    if (!isSuperUser) return;
    await updateSwivuAssist();
  };

  const handleUpdateBoard = async () => {
    if (!canEdit || !isValidUrl) return;

    const selectedBoardId = selectedBoard?.record?.id;
    const currentValue = userPreferences?.getSetting(PERSISTENCE_STATE_KEYS.swivuAssistSheetUrl, "", true);

    // If the value hasn't changed, just update the content without changing the setting
    if (currentValue !== googleSheetUrl) {
      const record = project?.boardPreferencesStore?.find((r: any) => r.key === PERSISTENCE_STATE_KEYS.swivuAssistSheetUrl);

      // Listen for sync completion
      const handleSync = async (e: any) => {
        const hasUpdatedRecord = e.response.boardpreferences?.rows?.find((up: any) => up.key === PERSISTENCE_STATE_KEYS.swivuAssistSheetUrl);
        const hasRemovedRecord = e.response.boardpreferences?.removed && record && record.key === PERSISTENCE_STATE_KEYS.swivuAssistSheetUrl;

        if (hasUpdatedRecord || hasRemovedRecord) {
          await updateSwivuAssist(selectedBoardId?.toString());
        }
      };

      project?.on?.({
        sync: handleSync,
        once: true
      });

      if (googleSheetUrl === "" && record) {
        project?.boardPreferencesStore?.remove(record.id);
      } else {
        userPreferences?.updateSetting(PERSISTENCE_STATE_KEYS.swivuAssistSheetUrl, String(googleSheetUrl), true);
      }
    } else {
      await updateSwivuAssist(selectedBoardId?.toString());
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
            <p className="text-sm text-muted-foreground">
              {subcategory.description}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-5 max-w-3xl">
        {/* Instructions Card */}
        <Card className="py-3 px-4">
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">How to Customize</h3>
              <p className="text-xs text-muted-foreground">Follow these steps to set up your custom help content</p>
            </div>

            <div className="space-y-5">
              {ASSIST_STEPS.map((step) => (
                <div key={step.number} className="space-y-2">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-sm">
                      <span className="text-sm font-bold">{step.number}</span>
                    </div>
                    <div className="flex-1 pt-0.5">
                      <h4 className="font-semibold text-base text-foreground mb-1.5">{step.title}</h4>
                      {Array.isArray(step.content) ? (
                        <div className="space-y-1">
                          {step.content.map((text, i) => (
                            <p key={i} className="text-sm text-muted-foreground">
                              {text.includes("IMPORTANT") ? (
                                <>
                                  <span className="text-red-600 dark:text-red-400 font-medium">IMPORTANT:</span>
                                  {text.replace("IMPORTANT:", "")}
                                </>
                              ) : (
                                text
                              )}
                            </p>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">{step.content}</p>
                      )}

                      {step.hasLink && (
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <a
                            href={`${SERVER_BASE_URL}/swivuassist/template-url`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            Open Swivu Assist Template
                          </a>
                          <Button variant="ghost" size="sm" onClick={handleCopyTemplateLink} className="h-7 px-2 text-xs">
                            <Copy className="h-3 w-3 mr-1" />
                            Copy Link
                          </Button>
                        </div>
                      )}

                      {step.tip && (
                        <div className="mt-2 flex items-start gap-2 p-2 rounded-md bg-muted/50">
                          <Info className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-muted-foreground italic">{step.tip}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Configuration Card */}
        <Card className="py-3 px-4">
          <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
               <div>
                 <h3 className="text-base font-semibold text-foreground mb-1">Google Sheet Link</h3>
                 <p className="text-xs text-muted-foreground">Paste your Google Sheets link here to use custom help content</p>
               </div>
               {!isLoading && hasCustomContent && updateSuccess && (
                 <div className="flex-shrink-0">
                   <div className="px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                     <CheckCircle2 className="h-3 w-3" />
                     <span>Custom Content Active</span>
                   </div>
                 </div>
               )}
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="google-sheet-url" className="flex items-center gap-2">
                    <Link2 className="h-4 w-4 text-muted-foreground" />
                    Sheet URL
                  </Label>
                  {googleSheetUrl && canEdit && (
                    <Button variant="ghost" size="sm" onClick={handleClearUrl} className="h-7 px-2 text-xs">
                      <X className="h-3 w-3 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>
                <div className="relative">
                  <Input
                    id="google-sheet-url"
                    type="url"
                    variant="background"
                    value={googleSheetUrl}
                    onChange={(e) => handleUrlChange(e.target.value)}
                    placeholder="https://docs.google.com/spreadsheets/d/..."
                    disabled={!canEdit || isLoading}
                    className={cn(
                      "pr-10",
                      !isValidUrl && googleSheetUrl && "border-red-500 focus-visible:ring-red-500",
                      isValidUrl && googleSheetUrl && "border-green-500 focus-visible:ring-green-500"
                    )}
                  />
                  {googleSheetUrl && updateSuccess && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {isValidUrl && <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />}
                    </div>
                  )}
                  {googleSheetUrl && !isValidUrl && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                    </div>
                  )}
                </div>
                {!isValidUrl && googleSheetUrl && (
                  <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-md">
                    <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Invalid Google Sheets URL</p>
                      <p className="text-xs mt-0.5 text-red-600/80 dark:text-red-400/80">Please enter a valid Google Sheets URL starting with https://docs.google.com/spreadsheets/</p>
                    </div>
                  </div>
                )}
                {isValidUrl && googleSheetUrl && updateSuccess && (
                  <div className="flex items-start gap-2 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 p-3 rounded-md">
                    <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <p className="font-medium">Successfully synced with Google Sheets</p>
                  </div>
                )}
              </div>

              {/* Reminder Info */}
              <Card className="py-3 px-4 border-primary/20 bg-primary/5">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <Info className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-foreground">
                      <strong>Reminder:</strong> Every time you make changes to your Google Sheet, click Update to sync.
                    </p>
                  </div>
                </div>
              </Card>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <div className="flex-1 flex flex-wrap gap-3">
                  {isSuperUser && (
                    <Button onClick={handleUpdateGlobal} disabled={isUpdating || isLoading} variant="outline" className="flex-1 sm:flex-none min-w-[140px]">
                      {isUpdating ? "Updating..." : "Update Global"}
                    </Button>
                  )}
                </div>
                <Button onClick={handleUpdateBoard} disabled={!canEdit || !isValidUrl || isUpdating || isLoading} className="flex-1 sm:flex-none min-w-[140px]">
                  {isUpdating ? "Updating..." : "Update"}
                </Button>
              </div>

              {!canEdit && (
                <Card className="py-3 px-4 border-primary/20 bg-primary/5">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <Info className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-foreground">
                        You need to be a <strong>Super User</strong> or <strong>Board Owner</strong> to customize Swivu Assist content.
                      </p>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SwivuAssistDetail;
